import useAsync from 'hooks/useAsync';
import { useEffect, useState } from 'react';
import { getAccountById } from 'actions/reports';
import { AccountHierarchy } from 'types/api/AccountHierarchy';
import { getObservationsCount } from 'actions/envScales/reports';
import { buildObservationsCountParams } from 'components/Reports/Observation/observationCountParams';
import { class2EditionAgeLevels, classAgeLevels } from 'constants/ageLevels';
import { UserReportConfiguration } from 'types/api/envScales/UserReportConfiguration';

type NodeMap = {
  [id: number]: string;
};

function mapAccountHierarchy(
  hierarchy: AccountHierarchy[],
  parent?: AccountHierarchy
) {
  let nodesMap: NodeMap = {};

  hierarchy.forEach(node => {
    if (node.type !== 'Classroom') {
      nodesMap[node.id] = node.name;
    } else {
      nodesMap[node.id] = `${node.name} (${parent?.name})`;
    }

    const childMap = mapAccountHierarchy(node.children, node);
    nodesMap = { ...nodesMap, ...childMap };
  });

  return nodesMap;
}

export function useHierarchyNames(accountId: number) {
  const [nodes, setNodes] = useState<NodeMap>({});

  useEffect(() => {
    async function fetchAccount() {
      const { data: account } = await getAccountById(accountId);
      const nodesMap = mapAccountHierarchy(account.hierarchy);
      setNodes(nodesMap);
    }

    fetchAccount();
  }, [accountId]);

  return nodes;
}

export function useAgeLevelName(abbreviation: string) {
  const allAgeLevels = [...classAgeLevels, ...class2EditionAgeLevels];
  const ageLevel = allAgeLevels.find(age => age.value === abbreviation);

  return String(ageLevel?.text ?? '');
}

type ConfigParams = Pick<
  UserReportConfiguration,
  | 'start_date'
  | 'end_date'
  | 'hierarchy_selections'
  | 'age_level'
  | 'include_archived_classrooms'
  | 'include_archived_nodes'
>;

export function useConfigTotal(
  config: ConfigParams | null,
  archivedOptionsSource?: ConfigParams | null
) {
  const { run, data } = useAsync();
  const archivedSource = archivedOptionsSource ?? config;

  useEffect(() => {
    if (!config) {
      return;
    }

    run(
      getObservationsCount(
        buildObservationsCountParams({
          startDate: config.start_date,
          endDate: config.end_date,
          ageLevel: config.age_level,
          nodeIds: config.hierarchy_selections,
          includeArchivedClassrooms:
            archivedSource?.include_archived_classrooms,
          includeArchivedNodes: archivedSource?.include_archived_nodes
        })
      )
    );
  }, [run, config, archivedSource]);

  return data?.total_observations ?? 0;
}
